import { User, AuthError } from '../types'

// JWT token management
class AuthService {
  private readonly TOKEN_KEY = 'devwhisper_token'
  private readonly REFRESH_TOKEN_KEY = 'devwhisper_refresh_token'
  private readonly USER_KEY = 'devwhisper_user'
  private readonly TOKEN_EXPIRY = 24 * 60 * 60 * 1000 // 24 hours
  private readonly REFRESH_TOKEN_EXPIRY = 7 * 24 * 60 * 60 * 1000 // 7 days

  // Get current user
  getCurrentUser(): User | null {
    try {
      const userStr = localStorage.getItem(this.USER_KEY)
      if (!userStr) return null
      
      const user = JSON.parse(userStr)
      const token = this.getToken()
      
      if (!token || this.isTokenExpired(token)) {
        this.logout()
        return null
      }
      
      return user
    } catch (error) {
      console.error('Error getting current user:', error)
      this.logout()
      return null
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const user = this.getCurrentUser()
    return user !== null
  }

  // Login with email/password
  async login(email: string, password: string): Promise<User> {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new AuthError(error.message || 'Login failed')
      }

      const data = await response.json()
      this.setTokens(data.token, data.refreshToken)
      this.setUser(data.user)
      
      return data.user
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  // Register new user
  async register(email: string, password: string, name: string): Promise<User> {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new AuthError(error.message || 'Registration failed')
      }

      const data = await response.json()
      this.setTokens(data.token, data.refreshToken)
      this.setUser(data.user)
      
      return data.user
    } catch (error) {
      console.error('Registration error:', error)
      throw error
    }
  }

  // Google OAuth login
  async loginWithGoogle(): Promise<void> {
    try {
      // Get the OAuth URL from backend
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/google`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new AuthError(error.message || 'Failed to get Google OAuth URL')
      }

      const data = await response.json()
      // Redirect to Google OAuth
      window.location.href = data.authUrl
    } catch (error) {
      console.error('Google login error:', error)
      throw error
    }
  }

  // Handle OAuth callback
  async handleOAuthCallback(code: string): Promise<User> {
    try {
      console.log('handleOAuthCallback - API URL:', `${import.meta.env.VITE_API_URL}/api/auth/google/callback`);
      console.log('handleOAuthCallback - Code:', code);
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/google/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      })

      console.log('handleOAuthCallback - Response status:', response.status);
      console.log('handleOAuthCallback - Response ok:', response.ok);

      if (!response.ok) {
        const error = await response.json()
        console.error('handleOAuthCallback - Error response:', error);
        throw new AuthError(error.message || 'OAuth callback failed')
      }

      const data = await response.json()
      console.log('handleOAuthCallback - Success data:', data);
      this.setTokens(data.token, data.refreshToken)
      this.setUser(data.user)
      
      return data.user
    } catch (error) {
      console.error('OAuth callback error:', error)
      throw error
    }
  }

  // Refresh token
  async refreshToken(): Promise<string> {
    try {
      const refreshToken = this.getRefreshToken()
      if (!refreshToken) {
        throw new AuthError('No refresh token available')
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      })

      if (!response.ok) {
        throw new AuthError('Token refresh failed')
      }

      const data = await response.json()
      this.setTokens(data.token, data.refreshToken)
      
      return data.token
    } catch (error) {
      console.error('Token refresh error:', error)
      this.logout()
      throw error
    }
  }

  // Logout
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY)
    localStorage.removeItem(this.REFRESH_TOKEN_KEY)
    localStorage.removeItem(this.USER_KEY)
    
    // Clear any session data
    sessionStorage.clear()
    
    // Redirect to login
    window.location.href = '/login'
  }

  // Get auth token for API calls
  async getAuthToken(): Promise<string | null> {
    const token = this.getToken()
    
    if (!token) {
      return null
    }

    if (this.isTokenExpired(token)) {
      try {
        return await this.refreshToken()
      } catch (error) {
        this.logout()
        return null
      }
    }

    return token
  }

  // Update user profile
  async updateProfile(updates: Partial<User>): Promise<User> {
    try {
      const token = await this.getAuthToken()
      if (!token) {
        throw new AuthError('Not authenticated')
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new AuthError(error.message || 'Profile update failed')
      }

      const user = await response.json()
      this.setUser(user)
      
      return user
    } catch (error) {
      console.error('Profile update error:', error)
      throw error
    }
  }

  // Change password
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    try {
      const token = await this.getAuthToken()
      if (!token) {
        throw new AuthError('Not authenticated')
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new AuthError(error.message || 'Password change failed')
      }
    } catch (error) {
      console.error('Password change error:', error)
      throw error
    }
  }

  // Private methods
  private getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY)
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY)
  }

  private setTokens(token: string, refreshToken: string): void {
    localStorage.setItem(this.TOKEN_KEY, token)
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken)
  }

  private setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user))
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.exp * 1000 < Date.now()
    } catch (error) {
      return true
    }
  }
}

export default new AuthService() 